import React, { useState, useEffect } from 'react';
import { Transition } from '@headlessui/react';
import { CheckCircleIcon, XCircleIcon, InformationCircleIcon, XMarkIcon } from '@heroicons/react/24/solid';
import useApi from '../hooks/useApi';
import AffiliateSidebarRail from './ads/AffiliateSidebarRail';
import NativeAffiliateBanner from './ads/NativeAffiliateBanner';

const MAX_PDF_SIZE_BYTES = 5 * 1024 * 1024;
const MAX_PDF_SIZE_LABEL = '5 MB';

export default function UploadPage({updateShowLogin,onShowSignUp}) {
    
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState('');
  const [uploadQueue, setUploadQueue] = useState([]);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const { request } = useApi();
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) {
      setSelectedFile(null);
      return;
    }
    if (file.size > MAX_PDF_SIZE_BYTES) {
      setSelectedFile(null);
      setStatus('error');
      setMessage(`PDF must be ${MAX_PDF_SIZE_LABEL} or smaller.`);
      e.target.value = '';
      return;
    }
    setMessage('');
    setStatus('');
    setSelectedFile(file);
  };
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loadingQueue, setLoadingQueue] = useState(true);
  const [isPremium, setIsPremium] = useState(false);
  const [uploadsUsed, setUploadsUsed] = useState(0);
  const [status, setStatus] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    const premiumStatus = localStorage.getItem('is_premium');
    setIsLoggedIn(!!token);
    setIsPremium(premiumStatus === '1');
  }, []);

  const handleUpload = async () => {
    if (!selectedFile) return;
    setMessage('');
    if (selectedFile.size > MAX_PDF_SIZE_BYTES) {
      setMessage(`PDF must be ${MAX_PDF_SIZE_LABEL} or smaller.`);
      setStatus('error');
      return;
    }
    if(title === ''){
      setMessage('Please enter title!');
      setStatus('error');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('title', title);
      formData.append('description', description);
      formData.append('is_only_snapshot',1)

      const data = await request({
        method: 'post',
        url: `api/v1/pdf_parser/parse_pdf/`,
        data: formData,
        withAuth:true,
      });

    if (data.message) {
        setMessage(data.message);
        setTitle('');
        setDescription('');
        setSelectedFile(null);
        setStatus('success');
    } 
    } catch (err) {
      console.error('Upload error:', err);
      const data = err.response?.data;
      if (data?.message) {
        setMessage(data.message);
      } else if (data?.file) {
        setMessage(Array.isArray(data.file) ? data.file[0] : data.file);
      } else {
        setMessage('Error occurred during upload.');
      }
      setStatus('error');
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    const interval = setInterval(async () => {
        try {
          const res = await request({
            method: 'post',
            url: `/api/v1/pdf_parser/task_progress/`,
            withAuth:true,
            data:{
              task:'book_upload'
            }
          });
          if (res.tasks) {
            setUploadQueue(res.tasks);
            setUploadsUsed(res.tasks_count || 0);
          }
        } catch (err) {
          console.error('Polling error:', err);
        }finally {
          setLoadingQueue(false);
        }
    }, 5000); // every 5 seconds
    return () => clearInterval(interval);
  }, [uploadQueue]);

  useEffect(() => {
    if (!message) return;
    const timer = setTimeout(() => setMessage(''), 5000);
    return () => clearTimeout(timer);
  }, [message]);

  const getStatusColor = (status) => {
    if (status > 0 && status < 100) {
      return 'text-violet-900';
    } else if (status === 100) {
      return 'text-green-600';
    } else if (status < 0) {
      return 'text-red-600';
    } else {
      return 'text-gray-600';
    }
  };

  const getStatus = (status) => {
    if (status > 0 && status < 100) {
      return `In progress (${Math.round(status)}%)`;
    } else if (status === 100) {
      return 'Completed';
    } else if (status < 0) {
      return 'Failed';
    } else {
      return 'Queued';
    }
  };  

  return (
    <div className="mx-auto mt-10 max-w-6xl px-4 pb-10">
      <div className="flex items-start justify-center gap-6 xl:gap-8">
        <AffiliateSidebarRail placement="upload_left" salt={0} productCount={2} />

        <div className="w-full max-w-2xl shrink-0 border rounded shadow bg-white p-6">
          {!isPremium && uploadsUsed >= 10 && (
            <div className="bg-yellow-100 border border-yellow-300 text-yellow-800 px-4 py-2 rounded mb-4 text-sm">
              You’ve reached your free upload limit (10 books). Upgrade to enjoy unlimited uploads.
            </div>
          )}

          {isLoggedIn ? (
            <>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold text-gray-800">Upload Your PDF</h2>
                {!isPremium && (
                  <div className="text-right text-sm">
                    <p className="text-gray-600 mb-1">
                      {uploadsUsed} / 10 uploads used
                    </p>
                    <button
                      onClick={() => window.location.href = '/pricing'}
                      className="text-violet-900 hover:underline text-sm font-medium"
                    >
                      Want unlimited? Go Premium →
                    </button>
                  </div>
                )}
              </div>
              <input
                type="text"
                placeholder="Enter book title"
                value={title}
                onChange={(e) => {
                  setTitle(e.target.value);
                  setMessage('');
                }}
                className="mb-4 block w-full border rounded px-3 py-2"
              />
              <textarea
                placeholder="Enter book description"
                value={description}
                onChange={(e) => {
                  setDescription(e.target.value);
                  setMessage('');
                }}
                className="mb-4 block w-full border rounded px-3 py-2"
                rows={3}
              />
              <input
                type="file"
                accept="application/pdf"
                onChange={handleFileChange}
                className="mb-1 block w-full"
              />
              <p className="mb-4 text-xs text-gray-500">Maximum file size: {MAX_PDF_SIZE_LABEL}</p>
              <button
                onClick={handleUpload}
                disabled={uploading || !selectedFile}
                className="bg-violet-900 text-white px-4 py-2 rounded hover:bg-violet-950 disabled:opacity-50"
              >
                {uploading ? 'Uploading...' : 'Upload'}
              </button>
              <Transition
                show={!!message}
                enter="transition ease-out duration-300"
                enterFrom="opacity-0 -translate-y-1"
                enterTo="opacity-100 translate-y-0"
                leave="transition ease-in duration-500"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 -translate-y-1"
              >
                <div
                  className={`mt-4 flex items-start gap-3 rounded-lg border px-4 py-3 text-sm shadow-sm ${
                    status === 'success'
                      ? 'border-green-200 bg-green-50 text-green-800'
                      : status === 'error'
                      ? 'border-red-200 bg-red-50 text-red-800'
                      : 'border-violet-200 bg-violet-50 text-violet-950'
                  }`}
                  role="status"
                >
                  {status === 'success' ? (
                    <CheckCircleIcon className="h-5 w-5 flex-shrink-0 text-green-500" />
                  ) : status === 'error' ? (
                    <XCircleIcon className="h-5 w-5 flex-shrink-0 text-red-500" />
                  ) : (
                    <InformationCircleIcon className="h-5 w-5 flex-shrink-0 text-violet-800" />
                  )}
                  <span className="flex-1">{message}</span>
                  <button
                    type="button"
                    onClick={() => setMessage('')}
                    className="flex-shrink-0 rounded p-0.5 opacity-60 transition hover:opacity-100"
                    aria-label="Dismiss notification"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </div>
              </Transition>
              {/* Upload Queue */}
              <div className="mt-8">
                <h3 className="text-lg font-semibold mb-2">Upload Queue</h3>

                {loadingQueue ? (
                  <div className="flex justify-center py-4">
                    <div className="w-6 h-6 border-4 border-violet-800 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                ) : uploadQueue.length === 0 ? (
                  <div className="text-gray-500 text-sm text-center py-2">
                    No items in the queue.
                  </div>
                ) : (
                  <div className="max-h-64 overflow-y-auto border rounded p-4 space-y-4 bg-gray-50">
                    {uploadQueue.map((upload) => (
                      <div
                        key={upload.user_task_progress_unique_id}
                        className="flex items-center justify-between text-sm border-b pb-2"
                      >
                        <div className="flex flex-col">
                          <span className="font-medium text-gray-800">{upload.name}</span>
                        </div>
                        <div className={`font-semibold ${getStatusColor(upload.status)}`}>
                          {getStatus(upload.status)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <p className="mt-4 text-xs text-gray-500 italic text-center">
                * Please refresh "My Books" to see your newly uploaded books.
              </p>
            </>
          ) : (
            <div className="text-center space-y-6 py-16">
              <h2 className="text-3xl font-bold text-gray-800">Please log in to convert PDFs into Books and upload themes</h2>
              <p className="text-gray-600 text-lg">Don't have an account? You can create one in seconds.</p>
              <div className="flex justify-center gap-4">
                <button
                  onClick={updateShowLogin}
                  className="bg-violet-900 text-white px-6 py-2 rounded hover:bg-violet-950 transition"
                >
                  Log In
                </button>
                <button
                  onClick={onShowSignUp}
                  className="bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700 transition"
                >
                  Sign Up
                </button>
              </div>
            </div>
          )}
        </div>

        <AffiliateSidebarRail placement="upload_right" salt={3} productCount={2} />
      </div>

      {/* Side rails are desktop-only; keep a light strip on smaller screens. */}
      <div className="mt-8 lg:hidden">
        <NativeAffiliateBanner placement="upload_mobile" />
      </div>
    </div>
  );  
}
